import * as Sentry from '@sentry/react';
import { makeStyles } from '@fluentui/react-components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLoader, DebugInfoOverlay, ErrorFallback } from '@components';
import { TIMING_METRICS } from '@constants';
import { useLoadCachedConfiguration } from '@data';
import { useInitApp, SharedMailboxContextProvider } from '@utils';
import { AuthGate } from './auth';
import { ConfigurationGate } from './configuration';
import { AppRoutes } from './routes';
import { useDocumentTitle } from './hooks/useDocumentTitle.hook';
import { useTimingMetric } from './hooks/useTimingMetric.hook';

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
  },
});

const App = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { isI18NextInitialized, mailboxStatus } = useInitApp();
  const { startTimingMetric } = useTimingMetric();

  useLoadCachedConfiguration();
  useDocumentTitle(isI18NextInitialized);

  useEffect(() => {
    startTimingMetric(TIMING_METRICS.REPORT_DURATION);
  }, []);

  if (!isI18NextInitialized) {
    return null;
  }

  if (mailboxStatus === 'timeout') {
    return (
      <>
        <DebugInfoOverlay />
        <ErrorFallback
          title={t('errorPage.mailbox.title')}
          subtitle={t('errorPage.mailbox.subtitle')}
        />
      </>
    );
  }

  if (mailboxStatus === 'loading') {
    return (
      <>
        <DebugInfoOverlay />
        <AppLoader />
      </>
    );
  }

  return (
    <SharedMailboxContextProvider>
      <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
        <DebugInfoOverlay />
        <main className={styles.root}>
          <AuthGate>
            <ConfigurationGate>
              <AppRoutes />
            </ConfigurationGate>
          </AuthGate>
        </main>
      </Sentry.ErrorBoundary>
    </SharedMailboxContextProvider>
  );
};

export default App;
